/**
 * Suite Run Link
 *
 * @module features/releases/components/SuiteRunLink
 * @description The suite outcome on a timeline row's meta line, linking out to
 * the suite execution. Shared by release bands and build rows.
 */

import React from 'react';
import { Text, Tooltip, TooltipTrigger } from '@chatsdet/ui';
import { ArrowSquareOut } from '@phosphor-icons/react';
import type { TimelineSuiteRun } from '../hooks/useReleaseTimeline';
import { getSuitePassRate, suiteStatusLabels } from './suiteRun';

/**
 * The regression outcome on the band's meta line, linking out to the suite
 * execution when one is known. Carries the same result breakdown the old
 * `CommitSuiteGauge` tooltip showed — that tooltip moved here wholesale when
 * the node itself became the ship control and gave up the link.
 */
export function SuiteRunLink({
  suiteRun,
  label,
  colorClass,
  icon,
  title = 'Release suite',
}: {
  suiteRun: TimelineSuiteRun;
  label: string;
  colorClass: string;
  icon: React.ReactNode;
  /** Names the suite in the link's accessible name and tooltip. */
  title?: string;
}) {
  const passRate = getSuitePassRate(suiteRun);
  const isActive = suiteRun.status === 'queued' || suiteRun.status === 'running';
  const notRun = suiteRun.skippedWorkflows + suiteRun.degradedWorkflows;
  const passRateLabel = passRate === null ? 'No settled workflows' : `${passRate}% pass rate`;

  return (
    <TooltipTrigger placement="top" delay={100} offset={6}>
      <a
        href={`/runs/test/${suiteRun.executionId}`}
        target="_blank"
        rel="noopener noreferrer"
        onClick={(event) => event.stopPropagation()}
        aria-label={`Open ${title.toLowerCase()}: ${passRateLabel}, ${suiteRun.successfulWorkflows} passed and ${suiteRun.failedWorkflows} failed. Opens in a new tab.`}
        className={`inline-flex items-center gap-0.5 ${colorClass} hover:underline`}
      >
        {icon}
        <Text size="xs">{label}</Text>
      </a>
      <Tooltip variant="light" className="w-64 px-3 py-2.5 text-left">
        <div className="space-y-2">
          <div className="flex items-start justify-between gap-3">
            <div>
              <Text size="sm" weight="semibold">
                {title}
              </Text>
              <Text size="xs" className="text-muted-foreground">
                {suiteStatusLabels[suiteRun.status]} · {passRateLabel}
              </Text>
            </div>
            <ArrowSquareOut size={14} className="mt-0.5 flex-shrink-0 text-muted-foreground" />
          </div>
          <div className="grid grid-cols-2 gap-x-4 gap-y-1 border-t border-border pt-2 text-xs">
            <span className="text-muted-foreground">Passed</span>
            <span className="text-right font-medium tabular-nums text-foreground">
              {suiteRun.successfulWorkflows}
            </span>
            <span className="text-muted-foreground">Failed</span>
            <span className="text-right font-medium tabular-nums text-foreground">
              {suiteRun.failedWorkflows}
            </span>
            {suiteRun.inconclusiveWorkflows > 0 && (
              <>
                <span className="text-muted-foreground">Platform errors</span>
                <span className="text-right font-medium tabular-nums text-foreground">
                  {suiteRun.inconclusiveWorkflows}
                </span>
              </>
            )}
            {notRun > 0 && (
              <>
                <span className="text-muted-foreground">Not run</span>
                <span className="text-right font-medium tabular-nums text-foreground">
                  {notRun}
                </span>
              </>
            )}
            {isActive && (
              <>
                <span className="text-muted-foreground">Progress</span>
                <span className="text-right font-medium tabular-nums text-foreground">
                  {suiteRun.completedWorkflows}/{suiteRun.totalWorkflows}
                </span>
              </>
            )}
          </div>
          <Text size="xs" className="text-muted-foreground">
            Click to open the suite in a new tab
          </Text>
        </div>
      </Tooltip>
    </TooltipTrigger>
  );
}
